import { useEffect, useMemo, useRef, useState } from "react"
import { SparklesIcon, TagIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { TagCategoryRow } from "~/ui-rtk/components/ui/charts/TableVisualization/components/TagPanel/TagCategoryRow"
import { useTagCategories } from "~/ui-rtk/hooks/use-structured-tags"
import type { TagCategory } from "~/ui-rtk/types/structured-tags"
import { useAdForgeGetDefaultsQuery } from "~/ui-rtk/api/adForgeApi"
import { composeFromTemplate, extractSlugs } from "./adNameTemplate"

interface AdNameBuilderProps {
  isOpen: boolean
  onClose: () => void
  /**
   * Apply the composed name to the editor's Ad Name field, along with the tag
   * selection that produced it (category slug → option ids) so the draft can
   * persist it and every launched ad gets the same tags.
   */
  onApply: (name: string, tags: Record<string, string[]>) => void
  /** Selection to restore when reopening, so edits are additive not destructive. */
  initialSelection?: Record<string, string[]>
  /**
   * When provided, renders a "Suggest with AI" button that runs the creative
   * analysis and merges the returned picks (category slug → option ids) into
   * the current selection. Rejections surface as an inline error.
   */
  suggestTags?: () => Promise<Record<string, string[]>>
}

const DELIMITERS: Array<{ value: string; label: string }> = [
  { value: "_", label: "Underscore  (a_b_c)" },
  { value: "-", label: "Hyphen  (a-b-c)" },
  { value: " | ", label: "Pipe  (a | b | c)" },
  { value: " ", label: "Space  (a b c)" },
]

/**
 * Composes an ad name from the company's existing structured tags. Reuses the
 * exact per-category selector (`TagCategoryRow`) from the paid-media
 * optimization page's tag panel — one row per category (in sort order, which
 * mirrors the DUER naming convention), predefined options as togglable pills,
 * user_generated categories with an inline "Add" to create a value. The chosen
 * options' LABELS are joined by the selected delimiter (default `_`) into a
 * live preview, then applied to the Ad Name field — e.g.
 * `Education/Empowerment_Dave Video_Health Hack_Biohacker_1+ min_Good Day NYC 1`.
 * The selection is returned to the caller alongside the name: it's saved on the
 * AdCreative draft and applied as TagAssignments on every ad launched from it,
 * so an ad is tagged the moment it exists rather than back-filled later from
 * its name. Nothing is written to TagAssignment from this modal itself.
 * (Creating a new option via "Add" does persist that option, same as the panel.)
 *
 * When the company has set an ad-name template (AdForge defaults), the modal
 * instead renders only the categories the template references — in template
 * order — and composes the name by substituting each `{slug}` placeholder with
 * the selected label(s); empty placeholders and their separators are dropped.
 */
export default function AdNameBuilder({
  isOpen,
  onClose,
  onApply,
  initialSelection,
  suggestTags,
}: AdNameBuilderProps) {
  const { categories, isLoading, createOption } = useTagCategories()
  // Company-level ad-name template ({slug} placeholders). When set, the name is
  // composed by substituting selected tag labels into it instead of a plain
  // delimiter join. Loaded once the modal opens.
  const { data: defaults } = useAdForgeGetDefaultsQuery(undefined, {
    skip: !isOpen,
  })
  const nameTemplate = defaults?.nameTemplate?.trim() ?? ""
  const hasTemplate = nameTemplate.length > 0
  // category slug -> selected option ids (click order preserved for the name).
  const [selected, setSelected] = useState<Record<string, string[]>>(
    initialSelection ?? {},
  )
  // Labels for options created in this session (not yet in the cached category).
  const [extraLabels, setExtraLabels] = useState<Record<string, string>>({})
  const [delimiter, setDelimiter] = useState("_")
  const [isSuggesting, setIsSuggesting] = useState(false)
  const [suggestError, setSuggestError] = useState<string | null>(null)

  const handleSuggest = async () => {
    if (!suggestTags || isSuggesting) return
    setIsSuggesting(true)
    setSuggestError(null)
    try {
      const suggested = await suggestTags()
      if (Object.keys(suggested).length === 0) {
        setSuggestError("No matching tags were suggested for this creative.")
        return
      }
      setSelected(prev => ({ ...prev, ...suggested }))
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ??
        (err as Error)?.message ??
        "Couldn't analyze this creative."
      setSuggestError(message)
    } finally {
      setIsSuggesting(false)
    }
  }

  const labelFor = (
    category: TagCategory,
    optionId: string,
  ): string | undefined =>
    category.options.find(o => o.id === optionId)?.label ??
    extraLabels[optionId]

  // Categories rendered as selector rows. With a template we render only the
  // categories it references, in template order, so the picker mirrors the
  // rule. Otherwise every category in its natural sort order.
  const ordered = useMemo(() => {
    if (hasTemplate) {
      const bySlug = new Map(categories.map(c => [c.slug, c]))
      return extractSlugs(nameTemplate)
        .map(slug => bySlug.get(slug))
        .filter((c): c is TagCategory => !!c)
    }
    return [...categories].sort((a, b) => a.sortOrder - b.sortOrder)
  }, [categories, hasTemplate, nameTemplate])

  // Selected labels per category, joined (within a category) by the chosen
  // separator. Labels are used verbatim to match the company's convention.
  const labelsFor = (category: TagCategory): string =>
    (selected[category.slug] ?? [])
      .map(id => labelFor(category, id))
      .map(label => label?.trim())
      .filter((label): label is string => !!label)
      .join(delimiter)

  // Template mode: substitute selected labels into the {slug} placeholders.
  // Plain mode: flatten selected labels across categories (category order, then
  // click order) and join by the chosen separator.
  const preview = hasTemplate
    ? composeFromTemplate(
        nameTemplate,
        Object.fromEntries(ordered.map(c => [c.slug, labelsFor(c)])),
      )
    : ordered
        .map(category => labelsFor(category))
        .filter(value => value.length > 0)
        .join(delimiter)

  // Restore the draft's saved selection each time the modal opens. The
  // component stays mounted between opens, so without this a user who cancels
  // (or reopens after saving) would see stale local picks rather than what the
  // draft actually carries.
  // Deliberately keyed on `isOpen` alone: re-running on `initialSelection`
  // identity would clobber in-progress picks on every parent render.
  const selectionRef = useRef(initialSelection)
  selectionRef.current = initialSelection
  useEffect(() => {
    if (isOpen) setSelected(selectionRef.current ?? {})
  }, [isOpen])

  // Close on Escape (outside-click is handled by the backdrop's onClick).
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 backdrop-blur-sm overflow-y-auto py-10"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl mx-4 bg-system-gray-900 rounded-xl border border-system-gray-800 shadow-2xl flex flex-col max-h-[85vh]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-system-gray-800 flex-shrink-0">
          <div className="flex items-center gap-2">
            <TagIcon className="w-5 h-5 text-brand" />
            <h2 className="text-lg font-semibold text-white">
              Build ad name from tags
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {suggestTags && (
              <button
                type="button"
                onClick={() => void handleSuggest()}
                disabled={isSuggesting || isLoading}
                title="Analyze the creative with AI and pre-select matching tags"
                className="btn btn-outline btn-xs gap-1 border-brand/60 text-brand hover:border-brand hover:bg-brand/10 disabled:opacity-50"
              >
                {isSuggesting ? (
                  <span className="loading loading-spinner loading-xs" />
                ) : (
                  <SparklesIcon className="w-3.5 h-3.5" />
                )}
                {isSuggesting ? "Analyzing…" : "Suggest with AI"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost btn-xs btn-circle"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        {suggestError && (
          <p className="px-6 pt-2 text-xs text-system-red-400">
            {suggestError}
          </p>
        )}

        <div className="px-6 py-2 overflow-y-auto flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-system-gray-400 gap-2">
              <span className="loading loading-spinner loading-sm" />
              Loading tags…
            </div>
          ) : ordered.length === 0 ? (
            <p className="text-sm text-system-gray-400 py-8 text-center">
              No tags found for this company. Add tag categories first, then
              come back to compose names from them.
            </p>
          ) : (
            ordered.map(category => (
              <TagCategoryRow
                key={category.id}
                category={category}
                selectedOptionIds={selected[category.slug] ?? []}
                onChange={ids =>
                  setSelected(prev => ({ ...prev, [category.slug]: ids }))
                }
                onCreateOption={async label => {
                  const option = await createOption(category.slug, label)
                  setExtraLabels(prev => ({
                    ...prev,
                    [option.id]: option.label,
                  }))
                  return option
                }}
              />
            ))
          )}
        </div>

        <div className="px-6 py-4 border-t border-system-gray-800 flex-shrink-0 space-y-3">
          {hasTemplate && (
            <div>
              <span className="text-xs text-system-gray-400">Template</span>
              <div className="mt-1 rounded-md bg-system-gray-950 border border-system-gray-800 px-3 py-1.5 font-mono text-xs text-system-gray-300 break-all">
                {nameTemplate}
              </div>
            </div>
          )}
          {!hasTemplate && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-system-gray-400 flex-shrink-0">
                Separator
              </span>
              <select
                value={delimiter}
                onChange={e => setDelimiter(e.target.value)}
                className="select select-xs py-0 bg-system-gray-800 border-system-gray-700 text-white focus:border-brand focus:outline-none [&>option]:bg-system-gray-800"
              >
                {DELIMITERS.map(d => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <span className="text-xs text-system-gray-400">Preview</span>
            <div className="mt-1 rounded-md bg-system-gray-950 border border-system-gray-700 px-3 py-2 font-mono text-sm text-white break-all min-h-[2.25rem]">
              {preview || (
                <span className="text-system-gray-600">
                  Pick tag values above to build a name…
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost btn-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                // Rendered categories come from this session's picks. Categories
                // the picker does NOT render (with a template, everything
                // outside it) can't be edited here, so their existing values
                // are carried over untouched — applying a name must never
                // silently strip tags the draft already carries.
                const rendered = new Set(ordered.map(c => c.slug))
                const applied: Record<string, string[]> = {}
                for (const [slug, ids] of Object.entries(
                  initialSelection ?? {},
                )) {
                  if (!rendered.has(slug) && ids.length > 0) applied[slug] = ids
                }
                for (const category of ordered) {
                  const ids = selected[category.slug] ?? []
                  if (ids.length > 0) applied[category.slug] = ids
                }
                onApply(preview, applied)
                onClose()
              }}
              disabled={!preview}
              className="btn btn-primary btn-sm gap-1 disabled:opacity-50"
            >
              Apply to ad name
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
